import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { NavLink } from '~/eds/src/types/NavLink';
import { useEntityTypes } from '~/features/managed-entities/entity-types/hooks/useEntityTypes';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useDashboardsSubnavs } from '~/hooks/useDashboardsSubnavs';
import { usePermissions } from '~/hooks/usePermissions';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { RbacPermission } from '~/types/Rbac';
import {
  buildSidebarAdminNavLink,
  createSidebarNavlinks,
} from '~/utils/navLinks';

const messages = defineMessages({
  home: {
    id: 'AppSidebarNav.home',
    defaultMessage: 'Home',
    description: 'Label for the sidebar link that navigates to the home page.',
  },
});

/**
 * Every permission the sidebar needs. Kept flag-independent: the managed
 * entities flag selects which *result* feeds Groups access, not which pairs to
 * ask for, and asking for a spare pair inside a batched request costs nothing.
 */
const NAV_PERMISSIONS: RbacPermission[] = [
  { resourceId: 'clause_library', resourceType: 'view' },
  { resourceId: 'clause_samples', resourceType: 'view' },
  { resourceId: 'conversational_ai', resourceType: 'edit' },
  { resourceId: 'document_x_ray', resourceType: 'view' },
  { resourceId: 'documents', resourceType: 'view' },
  { resourceId: 'documents_dashboard', resourceType: 'view' },
  { resourceId: 'entities', resourceType: 'view' },
  { resourceId: 'entity_groups', resourceType: 'view' },
  { resourceId: 'entity_types', resourceType: 'view' },
  { resourceId: 'workflow_templates', resourceType: 'view' },
  { resourceId: 'workflow_tickets', resourceType: 'view' },
  { resourceId: 'workflows_dashboard', resourceType: 'view' },
];

type Props = {
  /** Side effects the sidebar runs when a link navigates (resetting search,
   *  closing the mobile drawer) — owned by the component, not this hook. */
  onNavLinkClick: () => void;
};

/**
 * Resolves what the sidebar should show: the Workspace links, the pinned Home
 * and Admin entries, and whether Ask AI is available.
 *
 * Owns the permission and flag reads behind those decisions so the component
 * renders a list rather than recomputing the gating. `isLoading` reports the
 * permission round trip, so callers can tell "no access" apart from "not known
 * yet" — the two look identical in the items alone.
 */
export function useNavigationItems({ onNavLinkClick }: Props) {
  const intl = useIntl();
  const user = useCurrentUser();
  const { location } = useRouting();
  const { pathname } = location;
  const { dashboards } = useDashboardsSubnavs();

  const hasClauseLibrary = useFlag(FlagType.ClauseLibrary);
  const enableFieldAi = useFlag(FlagType.FieldAi);
  const hasDeprecateAnalyzer = useFlag(FlagType.DeprecateAnalyzer);
  const hasPlaybooks = useFlag(FlagType.Playbooks);
  const hasClmEvolutionPhase1 = useFlag(FlagType.CLMEvolutionPhase1);
  // The home route itself is flagged (EKP-58009), so the entry that points at
  // it has to be too — matching AppPageNavbar.
  const hasHome = useFlag(FlagType.Home);
  const hasMesPartiesMvp = useFlag(FlagType.MesPartiesMvp);
  const hasAskAnything = useFlag(FlagType.AskAnything);

  const { checkPermission, isLoading } = usePermissions({
    permissions: NAV_PERMISSIONS,
  });

  const hasEntityGroupsAccess = hasMesPartiesMvp
    ? checkPermission('entities', 'view') ||
      checkPermission('entity_types', 'view')
    : checkPermission('entity_groups', 'view');

  // EKP-58140: without this, the fetch 403s for any user lacking Groups access.
  const { entityTypes } = useEntityTypes({ skip: !hasEntityGroupsAccess });

  const dashboard = useSelector(
    slices.dashboardV2.selectors.selectActiveDashboard,
  );
  const activeSearch = useSelector(slices.searchV3.selectors.selectSavedSearch);
  const dashboardAndPathname = useMemo(
    () => ({ ...dashboard, pathname }),
    [dashboard, pathname],
  );

  const items = useMemo(
    () =>
      createSidebarNavlinks({
        currentUser: user,
        intl,
        onNavLinkClick,
        dashboards,
        activeSearch,
        hasClauseLibrary,
        hasDeprecateAnalyzer,
        hasPlaybooks,
        activeDashboard: dashboardAndPathname,
        hasClmEvolutionPhase1,
        permissions: {
          documents: checkPermission('documents', 'view'),
          entityGroups: hasEntityGroupsAccess,
          workflowTickets: checkPermission('workflow_tickets', 'view'),
          workflowBuilder: checkPermission('workflow_templates', 'view'),
          clauseLibrary: checkPermission('clause_library', 'view'),
          automation: checkPermission('clause_samples', 'view'),
          documentXRay:
            enableFieldAi && checkPermission('document_x_ray', 'view'),
          documentsDashboard: checkPermission('documents_dashboard', 'view'),
          workflowsDashboard: checkPermission('workflows_dashboard', 'view'),
          managedEntityParties: true,
        },
        activePathname: pathname,
        entityTypes,
      }),
    [
      user,
      intl,
      onNavLinkClick,
      dashboards,
      activeSearch,
      hasClauseLibrary,
      hasDeprecateAnalyzer,
      hasPlaybooks,
      dashboardAndPathname,
      hasClmEvolutionPhase1,
      entityTypes,
      checkPermission,
      hasEntityGroupsAccess,
      enableFieldAi,
      pathname,
    ],
  );

  const homeLink = useMemo<NavLink | null>(
    () =>
      hasHome
        ? {
            label: intl.formatMessage(messages.home),
            pathname: RoutePathType.Home,
            activePatterns: ['^/$'],
          }
        : null,
    [hasHome, intl],
  );

  // Pinned at the bottom of the sidebar, above the "A Workday Company"
  // credit — rather than scrolling with the rest of the Workspace links.
  const adminLink = useMemo(
    () => buildSidebarAdminNavLink(user, intl),
    [user, intl],
  );

  return {
    items,
    homeLink,
    adminLink,
    showAskAi: Boolean(
      hasAskAnything && checkPermission('conversational_ai', 'edit'),
    ),
    isLoading,
  };
}
